import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Mail, RefreshCw, GitBranch } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API } from './constants';
import { HubspotRecordLink } from './HubspotComponents';

export const InquiriesTab = ({ inquiries, setInquiries, hubspotStatus }) => {
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [filter, setFilter] = useState('all');
  const [syncingId, setSyncingId] = useState(null);
  const navigate = useNavigate();
  const portalId = hubspotStatus?.portal_id;

  const filteredInquiries = filter === 'all'
    ? inquiries
    : inquiries.filter(i => i.status === filter);

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${inquiryId}?status=${newStatus}`, {}, { withCredentials: true });
      setInquiries(prev => prev.map(i =>
        i.inquiry_id === inquiryId ? { ...i, status: newStatus } : i
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const syncToHubspot = async (inquiryId) => {
    setSyncingId(inquiryId);
    try {
      const res = await axios.post(
        `${API}/admin/inquiries/${inquiryId}/sync-hubspot`,
        {},
        { withCredentials: true }
      );
      setInquiries(prev => prev.map(i =>
        i.inquiry_id === inquiryId ? {
          ...i,
          hubspot_contact_id: res.data.hubspot_contact_id,
          hubspot_deal_id: res.data.hubspot_deal_id,
          hubspot_synced_at: res.data.synced_at,
        } : i
      ));
      toast.success('Synced to HubSpot');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'HubSpot sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const convertToCRM = async (inquiryId) => {
    try {
      await axios.post(`${API}/crm/from-inquiry/${inquiryId}`, {}, { withCredentials: true });
      toast.success('Converted to CRM client!');
      setInquiries(prev => prev.map(i =>
        i.inquiry_id === inquiryId ? { ...i, status: 'converted' } : i
      ));
      setSelectedInquiry(null);
      navigate('/admin/crm');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-heading text-2xl text-white">Inquiries</h2>
        <div className="flex gap-2">
          {['all', 'new', 'contacted', 'converted'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`text-xs uppercase tracking-widest px-4 py-2 transition-colors ${
                filter === status
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-[#0a0a0a] border border-white/10 text-white/60 hover:text-white'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {filteredInquiries.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-white/10 p-12 text-center">
          <Mail className="w-12 h-12 text-[#B8AEA1] mx-auto mb-4" />
          <p className="text-white/60">No inquiries found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry) => (
            <div
              key={inquiry.inquiry_id}
              className="bg-[#0a0a0a] border border-white/10 p-6 cursor-pointer hover:border-white/20"
              onClick={() => setSelectedInquiry(inquiry)}
              data-testid={`inquiry-${inquiry.inquiry_id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-white font-medium">{inquiry.name}</h3>
                    <span className={`text-xs uppercase px-2 py-1 ${
                      inquiry.status === 'new' ? 'bg-blue-50 text-blue-600' :
                      inquiry.status === 'contacted' ? 'bg-yellow-50 text-yellow-600' :
                      'bg-green-50 text-green-600'
                    }`}>
                      {inquiry.status}
                    </span>
                    {inquiry.hubspot_deal_id ? (
                      <HubspotRecordLink
                        portalId={portalId}
                        contactId={inquiry.hubspot_contact_id}
                        dealId={inquiry.hubspot_deal_id}
                      />
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          syncToHubspot(inquiry.inquiry_id);
                        }}
                        disabled={syncingId === inquiry.inquiry_id || !hubspotStatus?.connected}
                        className="inline-flex items-center gap-1 text-[#FF7A59] hover:text-[#FF9B82] text-xs disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        data-testid={`sync-hubspot-${inquiry.inquiry_id}`}
                        title={hubspotStatus?.connected ? 'Sync this inquiry to HubSpot' : 'HubSpot not connected'}
                      >
                        <RefreshCw size={12} className={syncingId === inquiry.inquiry_id ? 'animate-spin' : ''} />
                        {syncingId === inquiry.inquiry_id ? 'Syncing…' : 'Sync to HubSpot'}
                      </button>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">{inquiry.email} {inquiry.phone && `• ${inquiry.phone}`}</p>
                  <p className="text-white/60 text-sm mt-1">
                    {inquiry.event_type} {inquiry.event_date && `• ${inquiry.event_date}`} {inquiry.budget_range && `• ${inquiry.budget_range}`}
                  </p>
                </div>
                <p className="text-white/40 text-xs flex-shrink-0">
                  {format(new Date(inquiry.created_at), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inquiry Detail Modal */}
      <Dialog open={!!selectedInquiry} onOpenChange={() => setSelectedInquiry(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl">
          {selectedInquiry && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">
                  {selectedInquiry.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Email</p>
                    <p className="text-white">{selectedInquiry.email}</p>
                  </div>
                  {selectedInquiry.phone && (
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Phone</p>
                      <p className="text-white">{selectedInquiry.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Event Type</p>
                    <p className="text-white">{selectedInquiry.event_type}</p>
                  </div>
                  {selectedInquiry.event_date && (
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Event Date</p>
                      <p className="text-white">{selectedInquiry.event_date}</p>
                    </div>
                  )}
                  {selectedInquiry.budget_range && (
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Budget Range</p>
                      <p className="text-white">{selectedInquiry.budget_range}</p>
                    </div>
                  )}
                </div>

                {selectedInquiry.services_needed?.length > 0 && (
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Services Interested In</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedInquiry.services_needed.map((service) => (
                        <span key={service} className="text-xs bg-[#050505] text-white/80 px-3 py-1">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Message</p>
                  <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{selectedInquiry.message}</p>
                </div>

                <div>
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-2">Update Status</p>
                  <div className="flex gap-2">
                    {['new', 'contacted', 'converted'].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          updateStatus(selectedInquiry.inquiry_id, status);
                          setSelectedInquiry({ ...selectedInquiry, status });
                        }}
                        className={`text-xs uppercase tracking-widest px-4 py-2 transition-colors ${
                          selectedInquiry.status === status
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-[#050505] text-white/60 hover:text-white'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedInquiry.status !== 'converted' && (
                  <div className="pt-4 border-t border-white/10">
                    <button
                      onClick={() => convertToCRM(selectedInquiry.inquiry_id)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors"
                    >
                      <GitBranch size={16} />
                      Convert to CRM Pipeline
                    </button>
                    <p className="text-white/40 text-xs mt-2 text-center">
                      Add this inquiry to your client pipeline for tracking
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
